import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isGuestOnly } from '../../types/auth';
import { useWhatsNew } from '../../hooks/useWhatsNew';
import {
  HomeIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

interface BottomNavProps {
  onOpenSearch: () => void;
  onOpenMenu: () => void;
  menuOpen: boolean;
}

/**
 * Mobil alsó navigációs sáv (lg alatt). A "Több" gomb nyitja a felúszó menülapot.
 */
const BottomNav: React.FC<BottomNavProps> = ({ onOpenSearch, onOpenMenu, menuOpen }) => {
  const { hasUnseen: hasNews } = useWhatsNew();
  const location = useLocation();
  const { user } = useAuth();

  const linkClass = (active: boolean) =>
    `flex flex-1 flex-col items-center gap-0.5 pt-1 text-[10px] font-medium transition-colors ${
      active ? 'text-primary-600' : 'text-gray-500'
    }`;

  const iconBoxClass = (active: boolean) =>
    `flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
      active ? 'bg-primary-100' : ''
    }`;

  // A vendég a naptárat nézi; az eseménylista neki nem külön menüpont
  const links = isGuestOnly(user)
    ? [
        { name: 'Áttekintő', href: '/', icon: HomeIcon },
        { name: 'Naptár', href: '/calendar', icon: CalendarDaysIcon },
      ]
    : [
        { name: 'Áttekintő', href: '/', icon: HomeIcon },
        { name: 'Naptár', href: '/calendar', icon: CalendarDaysIcon },
        { name: 'Események', href: '/events', icon: ClipboardDocumentListIcon },
      ];

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 flex justify-around border-t border-gray-200 bg-white px-1 pt-1 pb-safe-1 shadow-[0_-1px_3px_rgba(0,0,0,0.06)] lg:hidden">
      {links.map((link) => {
        const active = location.pathname === link.href;
        return (
          <Link key={link.href} to={link.href} className={linkClass(active)}>
            <span className={iconBoxClass(active)}>
              <link.icon className="h-6 w-6" />
            </span>
            {link.name}
          </Link>
        );
      })}
      <button type="button" onClick={onOpenSearch} className={linkClass(false)}>
        <span className={iconBoxClass(false)}>
          <MagnifyingGlassIcon className="h-6 w-6" />
        </span>
        Keresés
      </button>
      <button type="button" onClick={onOpenMenu} className={linkClass(menuOpen)}>
        <span className={`relative ${iconBoxClass(menuOpen)}`}>
          <Squares2X2Icon className="h-6 w-6" />
          {/* A menülapon van a Funkciók és újdonságok - ide is jelezzük, ha van új */}
          {hasNews && <span className="absolute right-1 top-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white" />}
        </span>
        Több
      </button>
    </nav>
  );
};

export default BottomNav;
